import Breadcrumb from '../../components/feature/Breadcrumb';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../hooks/useSEO';
import { useLocation } from 'react-router-dom';

const BeginnerPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');

  useSEO({
    title: isEnglish ? 'For Beginners' : '初心者向け',
    description: isEnglish
      ? 'Beginner\'s guide to 3D printers. Explains basic knowledge including FDM mechanism, pre-purchase environment checks, required tools, and material selection.'
      : '3Dプリンター初心者向けガイド。FDM方式の仕組み、購入前の環境確認、必要な工具、材料の選び方など基礎知識を解説。',
    keywords: isEnglish
      ? '3D printer,beginner,FDM,purchase guide,basic knowledge,environment,tools'
      : '3Dプリンター,初心者,FDM,購入ガイド,基礎知識,環境,工具',
    canonicalPath: isEnglish ? '/en/beginner' : '/beginner'
  });

  const breadcrumbItems = isEnglish
    ? [
        { label: 'Home', path: '/en' },
        { label: 'For Beginners', path: '/en/beginner' }
      ]
    : [
        { label: 'ホーム', path: '/' },
        { label: '初心者向け', path: '/beginner' }
      ];

  const TEXT = isEnglish ? {
    pageTitle: 'For Beginners',
    whatIs: {
      title: 'What is a 3D Printer (Basic Explanation)',
      fdm: {
        title: 'FDM Mechanism (Melt and Stack)',
        desc: 'FDM (Fused Deposition Modeling) is a 3D printing technology that creates three-dimensional objects by melting plastic filament at high temperature and stacking thin layers. Molten plastic is extruded from a nozzle and formed layer by layer based on design data.'
      },
      proscons: {
        title: 'Advantages / Disadvantages',
        pros: 'Advantages',
        prosList: [
          'Relatively affordable and easy to introduce',
          'Wide variety of materials',
          'Can create complex shapes',
          'Ideal for prototyping',
          'Custom parts production'
        ],
        cons: 'Disadvantages',
        consList: [
          'Layer lines remain',
          'Long printing time',
          'Support material may be needed',
          'Limited accuracy',
          'Regular maintenance required'
        ]
      },
      uses: {
        title: 'Suitable Applications',
        prototype: {
          title: 'Prototyping',
          desc: 'Creating prototypes for design verification and functional testing'
        },
        custom: {
          title: 'Custom Parts',
          desc: 'Creating specialized parts that off-the-shelf products cannot handle'
        },
        education: {
          title: 'Education & Learning',
          desc: 'STEM education and acquiring design skills'
        }
      }
    },
    beforeBuying: {
      title: 'Things to Know Before Purchasing',
      environment: {
        title: 'Recommended Environment',
        list: [
          'Wi-Fi environment: Useful for remote operation if available',
          'Installation location: Noise and vibration are significant, so detached houses are preferable to apartments. Vibration and soundproofing measures are necessary for those concerned',
          'Ventilation: Odors and gases (especially ABS-based) often occur during operation, so a ventilated environment is desirable',
          'Pet safety: In environments with pets, models with enclosures are recommended for safety'
        ]
      },
      costs: {
        title: 'Expenses Beyond Unit Price',
        initial: 'Initial Costs',
        initialList: [
          'Filament (material)',
          'Basic tool set',
          'Workbench and installation space preparation',
          'Ventilation equipment (recommended)'
        ],
        running: 'Running Costs',
        runningList: [
          'Electricity',
          'Nozzle replacement',
          'Bed sheets',
          'Maintenance supplies'
        ]
      },
      materials: {
        title: 'Material Characteristics Summary',
        material: 'Material',
        difficulty: 'Difficulty',
        features: 'Features',
        uses: 'Uses',
        pla: {
          difficulty: 'Beginner',
          features: 'Easy to handle, low odor',
          uses: 'Prototypes, decorative items'
        },
        petg: {
          difficulty: 'Intermediate',
          features: 'Transparent, chemical resistant',
          uses: 'Containers, mechanical parts'
        },
        abs: {
          difficulty: 'Advanced',
          features: 'High strength, heat resistant',
          uses: 'Functional parts, tools'
        }
      },
      warranty: {
        title: 'Manufacturer Warranty / Support',
        intro: 'Since 3D printers are precision equipment, it is important to check the following points when purchasing:',
        list: [
          'Warranty period: Usually 1 year, may vary by part',
          'Technical support: Availability of Japanese language support, support hours',
          'Parts supply: Availability of consumables and replacement parts',
          'Community: Activity of user forums and information sharing'
        ]
      }
    },
    specs: {
      title: 'Specs to Check When Considering Purchase',
      buildSize: {
        title: 'Build Size',
        desc: '256×256mm has abundant third-party build plates and is easy to handle. Consider models 300mm or larger if large-scale printing is needed.'
      },
      temp: {
        title: 'Nozzle Temperature / Bed Temperature',
        desc: 'Important specs directly related to compatible filaments. Nozzle 300°C or higher and bed 110°C or higher can handle high-temperature materials like ABS and PC.'
      },
      wifi: {
        title: 'Wi-Fi Support',
        desc: 'Enables remote operation and progress monitoring. Many models support control from smartphone apps.'
      },
      autoLevel: {
        title: 'Auto-Leveling Support',
        desc: 'Automates bed leveling. Especially recommended for beginners, significantly reducing setup time.'
      },
      corexy: {
        title: 'CoreXY or Standard Cartesian System',
        desc: 'CoreXY is advantageous for high-speed printing but maintenance is somewhat complex. Cartesian system has simple structure and is beginner-friendly.'
      },
      enclosure: {
        title: 'Enclosure Presence',
        desc: 'Enclosed housing. Facilitates temperature management and is advantageous for warping-prone materials like ABS. Also improves safety.'
      },
      enclosureFeatures: {
        title: 'Additional Features with Enclosure',
        camera: {
          title: 'Built-in Camera',
          desc: 'Convenient for remote monitoring. Can monitor printing status in real-time.'
        },
        light: {
          title: 'Built-in Light',
          desc: 'Improves workability. Useful for checking details and taking photos.'
        },
        heatChamber: {
          title: 'Heat Chamber',
          desc: 'Advantageous for high-temperature materials like ABS and PC. Significantly reduces warping.'
        }
      },
      otherFeatures: {
        title: 'Other Convenient Features',
        ams: {
          title: 'AMS (Automatic Multi-Material Supply)',
          desc: 'System that automatically switches and supplies multiple filaments. Enables multi-color printing and automatic support material switching. Bambu Lab\'s AMS is representative, supporting up to 4 colors.'
        },
        dualNozzle: {
          title: 'Dual Nozzle / Quick Nozzle Change Mechanism',
          desc: 'Dual nozzle models enable simultaneous printing or combination of different materials with two nozzles. Quick nozzle change mechanism allows fast nozzle changes without tools, making diameter changes according to materials easy.'
        }
      }
    },
    toBuy: {
      title: 'Things to Buy at the Same Time',
      filament: {
        title: 'Filament',
        desc: 'PLA is recommended for beginners. 1-2 colors are sufficient.'
      },
      tools: {
        title: 'Hex Wrenches, Phillips Screwdriver',
        desc: 'Used for assembly and adjustment. Included standard with many 3D printers.'
      },
      grease: {
        title: 'Maintenance Grease',
        desc: 'Used for lubricating moving parts. Silicone-based is recommended.'
      },
      storage: {
        title: 'Filament Storage Supplies',
        desc: 'Filament box or vacuum pack. Essential for moisture protection.'
      },
      dryer: {
        title: 'Filament Dryer',
        desc: 'Dries moisture-absorbed filament. Important for maintaining print quality.'
      }
    },
    convenient: {
      title: 'Convenient Peripheral Items',
      caliper: {
        title: 'Digital Caliper',
        desc: 'Convenient for dimension measurement. Recommended ones that can measure in 0.01mm units.'
      },
      nipper: {
        title: 'Nippers',
        desc: 'Used for removing support material and cutting filament. Precision work type is convenient.'
      },
      hygro: {
        title: 'Thermometer / Humidity Management Supplies',
        desc: 'Important for managing filament storage environment. Also set with sealed container and desiccant. *Many 3D printers have built-in thermometers.'
      },
      nozzles: {
        title: 'Replacement Nozzles',
        desc: 'Nozzles of different diameters. 0.6mm or larger especially recommended for CF-based filaments. Prepare spares for wear.'
      },
      ams: {
        title: 'AMS (Multi-Material Device)',
        desc: 'Automatic switching of multiple filaments. Convenient for multi-color printing and efficiency.'
      },
      cutter: {
        title: 'Cutter',
        desc: 'For post-processing. Used for removing support material and fine adjustment work.'
      },
      driver: {
        title: 'Electric Screwdriver',
        desc: 'Convenient for nozzle replacement. Torque adjustment function recommended.'
      },
      rotary: {
        title: 'Rotary Tool',
        desc: 'For finishing work. Used for deburring, drilling, and polishing.'
      }
    }
  } : {
    pageTitle: '初心者向け',
    whatIs: {
      title: '3Dプリンターとは何か（基礎説明）',
      fdm: {
        title: 'FDM方式の仕組み（溶かして積む）',
        desc: 'FDM（熱溶解積層）方式は、プラスチックフィラメントを高温で溶かしながら、薄い層を積み重ねて立体物を作る3Dプリンティング技術です。ノズルから溶けたプラスチックを押し出し、設計データに基づいて一層ずつ造形していきます。'
      },
      proscons: {
        title: 'メリット／デメリット',
        pros: 'メリット',
        prosList: [
          '比較的安価で導入しやすい',
          '材料の種類が豊富',
          '複雑な形状も造形可能',
          '試作品製作に最適',
          'カスタムパーツの製作'
        ],
        cons: 'デメリット',
        consList: [
          '積層痕が残る',
          '造形時間が長い',
          'サポート材が必要な場合がある',
          '精度に限界がある',
          '定期的なメンテナンスが必要'
        ]
      },
      uses: {
        title: 'どんな用途に向くか',
        prototype: {
          title: '試作・プロトタイプ',
          desc: '設計検証や機能確認用の試作品製作'
        },
        custom: {
          title: 'カスタムパーツ',
          desc: '既製品では対応できない専用部品の製作'
        },
        education: {
          title: '教育・学習',
          desc: 'STEM教育や設計スキルの習得'
        }
      }
    },
    beforeBuying: {
      title: '購入前に知っておきたいこと',
      environment: {
        title: '推奨される環境',
        list: [
          'Wi-Fi環境：あると遠隔操作に使える',
          '設置場所：騒音と振動が大きいため、集合住宅よりは戸建てが望ましい。気になる人は振動・防音対策が必要',
          '換気：稼働時に異臭やガス（特にABS系）が発生することが多いため、換気できる環境が望ましい',
          'ペット対策：ペットがいる環境では、エンクロージャ付きの機種が安全性の面で推奨される'
        ]
      },
      costs: {
        title: '本体価格以外に必要な出費',
        initial: '初期費用',
        initialList: [
          'フィラメント（材料）',
          '基本工具セット',
          '作業台・設置場所の準備',
          '換気設備（推奨）'
        ],
        running: 'ランニングコスト',
        runningList: [
          '電気代',
          'ノズル交換',
          'ベッド用シート',
          'メンテナンス用品'
        ]
      },
      materials: {
        title: '材料の特性ざっくりまとめ',
        material: '材料',
        difficulty: '難易度',
        features: '特徴',
        uses: '用途',
        pla: {
          difficulty: '初級',
          features: '扱いやすい、臭いが少ない',
          uses: '試作品、装飾品'
        },
        petg: {
          difficulty: '中級',
          features: '透明、化学耐性',
          uses: '容器、機械部品'
        },
        abs: {
          difficulty: '上級',
          features: '高強度、耐熱性',
          uses: '機能部品、工具'
        }
      },
      warranty: {
        title: 'メーカー保証／サポートについて',
        intro: '3Dプリンターは精密機器のため、購入時は以下の点を確認することが重要です：',
        list: [
          '保証期間：通常1年、部品によって異なる場合がある',
          '技術サポート：日本語対応の有無、対応時間',
          '部品供給：消耗品や交換部品の入手しやすさ',
          'コミュニティ：ユーザーフォーラムや情報共有の活発さ'
        ]
      }
    },
    specs: {
      title: '購入検討する上でチェックしたいスペック',
      buildSize: {
        title: '造形サイズ',
        desc: '256×256mmはサードパーティ製ビルドプレートが豊富で扱いやすい。大型造形が必要な場合は300mm以上のモデルも検討。'
      },
      temp: {
        title: 'ノズル温度・ベッド温度',
        desc: '対応できるフィラメントに直結する重要スペック。ノズル300℃以上、ベッド110℃以上ならABS・PC等の高温材料にも対応可能。'
      },
      wifi: {
        title: 'Wi-Fi 対応の有無',
        desc: '遠隔操作や進捗確認が可能。スマートフォンアプリからの制御に対応している機種も多い。'
      },
      autoLevel: {
        title: 'オートレベリング対応の有無',
        desc: 'ベッドの水平調整を自動化。初心者には特に推奨される機能で、セットアップ時間を大幅に短縮。'
      },
      corexy: {
        title: 'CoreXY か、通常のCartesian方式か',
        desc: 'CoreXYは高速造形に有利だが、メンテナンスがやや複雑。Cartesian方式は構造がシンプルで初心者向き。'
      },
      enclosure: {
        title: 'エンクロージャの有無',
        desc: '密閉型筐体。温度管理が容易になり、ABS等の反りやすい材料に有利。安全性も向上。'
      },
      enclosureFeatures: {
        title: 'エンクロージャありの場合の追加機能',
        camera: {
          title: '内蔵カメラ',
          desc: '遠隔確認に便利。造形状況をリアルタイムで監視可能。'
        },
        light: {
          title: '内蔵ライト',
          desc: '作業性向上。細部の確認や写真撮影時に有用。'
        },
        heatChamber: {
          title: 'ヒートチャンバー',
          desc: 'ABS・PCなどの高温材料に有利。反りを大幅に軽減。'
        }
      },
      otherFeatures: {
        title: 'その他の便利機能',
        ams: {
          title: 'AMS（自動マルチマテリアル供給）',
          desc: '複数のフィラメントを自動で切り替えて供給するシステム。多色造形やサポート材の自動切り替えが可能。Bambu Lab社のAMSが代表的で、最大4色まで対応。'
        },
        dualNozzle: {
          title: 'デュアルノズル・クイックノズル交換機構',
          desc: 'デュアルノズル機種は2つのノズルで同時造形や異材料の組み合わせが可能。クイックノズル交換機構は工具不要で素早くノズル交換ができ、材料に応じた口径変更が容易。'
        }
      }
    },
    toBuy: {
      title: '同時に買っておくべきもの',
      filament: {
        title: 'フィラメント',
        desc: '初心者にはPLAがおすすめ。1〜2色あれば十分。'
      },
      tools: {
        title: '六角レンチ、＋ドライバー',
        desc: '組み立てや調整に使用。多くの3Dプリンターに標準で付属している。'
      },
      grease: {
        title: 'メンテナンス用グリス',
        desc: '可動部の潤滑に使用。シリコン系が推奨。'
      },
      storage: {
        title: 'フィラメント保管用品',
        desc: 'フィラメントボックスまたは真空パック。湿気対策に必須。'
      },
      dryer: {
        title: 'フィラメント乾燥機',
        desc: '吸湿したフィラメントを乾燥させる。造形品質の維持に重要。'
      }
    },
    convenient: {
      title: 'あると便利な周辺アイテム',
      caliper: {
        title: 'デジタルノギス',
        desc: '寸法測定に便利。0.01mm単位で測定できるものがおすすめ。'
      },
      nipper: {
        title: 'ニッパー',
        desc: 'サポート材の除去やフィラメントのカットに使用。精密作業用が便利。'
      },
      hygro: {
        title: '温度計／湿度管理用品',
        desc: 'フィラメントの保管環境管理に重要。密閉容器と乾燥剤もセットで。※3Dプリンター本体には温度計が内蔵されている場合が多い。'
      },
      nozzles: {
        title: '換えのノズル',
        desc: '口径違いのノズル。特にCF系フィラメントには0.6mm以上推奨。摩耗に備えて予備を用意。'
      },
      ams: {
        title: 'AMS（マルチマテリアル装置）',
        desc: '複数フィラメントの自動切り替え。多色造形や効率化に便利。'
      },
      cutter: {
        title: 'カッター',
        desc: '後処理用。サポート材の除去や細かい調整作業に使用。'
      },
      driver: {
        title: '電動ドライバー',
        desc: 'ノズル交換時に便利。トルク調整機能付きが推奨。'
      },
      rotary: {
        title: 'ルーター／リューター',
        desc: '仕上げ加工用。バリ取りや穴あけ、研磨作業に使用。'
      }
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* ページタイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.pageTitle}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 3Dプリンターとは何か */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-question-line text-accent mr-3"></i>
                {TEXT.whatIs.title}
              </h2>
              <div className="space-y-6 text-metal-light">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.whatIs.fdm.title}</h3>
                  <p className="leading-relaxed">
                    {TEXT.whatIs.fdm.desc}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.whatIs.proscons.title}</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-accent font-semibold mb-2">{TEXT.whatIs.proscons.pros}</h4>
                      <ul className="space-y-1 text-sm">
                        {TEXT.whatIs.proscons.prosList.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-accent-orange font-semibold mb-2">{TEXT.whatIs.proscons.cons}</h4>
                      <ul className="space-y-1 text-sm">
                        {TEXT.whatIs.proscons.consList.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.whatIs.uses.title}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2">{TEXT.whatIs.uses.prototype.title}</h4>
                      <p className="text-sm">{TEXT.whatIs.uses.prototype.desc}</p>
                    </div>
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2">{TEXT.whatIs.uses.custom.title}</h4>
                      <p className="text-sm">{TEXT.whatIs.uses.custom.desc}</p>
                    </div>
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2">{TEXT.whatIs.uses.education.title}</h4>
                      <p className="text-sm">{TEXT.whatIs.uses.education.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 購入前に知っておきたいこと */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-shopping-cart-line text-accent mr-3"></i>
                {TEXT.beforeBuying.title}
              </h2>
              <div className="space-y-6 text-metal-light">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.beforeBuying.environment.title}</h3>
                  <ul className="space-y-2 text-sm">
                    {TEXT.beforeBuying.environment.list.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.beforeBuying.costs.title}</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-accent font-semibold mb-2">{TEXT.beforeBuying.costs.initial}</h4>
                      <ul className="space-y-1 text-sm">
                        {TEXT.beforeBuying.costs.initialList.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-accent-orange font-semibold mb-2">{TEXT.beforeBuying.costs.running}</h4>
                      <ul className="space-y-1 text-sm">
                        {TEXT.beforeBuying.costs.runningList.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.beforeBuying.materials.title}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr>
                          <th>{TEXT.beforeBuying.materials.material}</th>
                          <th>{TEXT.beforeBuying.materials.difficulty}</th>
                          <th>{TEXT.beforeBuying.materials.features}</th>
                          <th>{TEXT.beforeBuying.materials.uses}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>PLA</td>
                          <td><span className="tech-label difficulty-beginner text-xs px-2 py-1">{TEXT.beforeBuying.materials.pla.difficulty}</span></td>
                          <td>{TEXT.beforeBuying.materials.pla.features}</td>
                          <td>{TEXT.beforeBuying.materials.pla.uses}</td>
                        </tr>
                        <tr>
                          <td>PETG</td>
                          <td><span className="tech-label difficulty-intermediate text-xs px-2 py-1">{TEXT.beforeBuying.materials.petg.difficulty}</span></td>
                          <td>{TEXT.beforeBuying.materials.petg.features}</td>
                          <td>{TEXT.beforeBuying.materials.petg.uses}</td>
                        </tr>
                        <tr>
                          <td>ABS</td>
                          <td><span className="tech-label difficulty-advanced text-xs px-2 py-1">{TEXT.beforeBuying.materials.abs.difficulty}</span></td>
                          <td>{TEXT.beforeBuying.materials.abs.features}</td>
                          <td>{TEXT.beforeBuying.materials.abs.uses}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.beforeBuying.warranty.title}</h3>
                  <p className="leading-relaxed mb-4">
                    {TEXT.beforeBuying.warranty.intro}
                  </p>
                  <ul className="space-y-2 text-sm">
                    {TEXT.beforeBuying.warranty.list.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* 購入検討する上でチェックしたいスペック */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-checkbox-line text-accent mr-3"></i>
                {TEXT.specs.title}
              </h2>
              <div className="space-y-6 text-metal-light">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="static-card p-4">
                    <h3 className="text-accent font-semibold mb-2 flex items-center">
                      <i className="ri-layout-grid-line mr-2"></i>
                      {TEXT.specs.buildSize.title}
                    </h3>
                    <p className="text-sm">
                      {TEXT.specs.buildSize.desc}
                    </p>
                  </div>
                  <div className="static-card p-4">
                    <h3 className="text-accent font-semibold mb-2 flex items-center">
                      <i className="ri-temp-hot-line mr-2"></i>
                      {TEXT.specs.temp.title}
                    </h3>
                    <p className="text-sm">
                      {TEXT.specs.temp.desc}
                    </p>
                  </div>
                  <div className="static-card p-4">
                    <h3 className="text-accent font-semibold mb-2 flex items-center">
                      <i className="ri-wifi-line mr-2"></i>
                      {TEXT.specs.wifi.title}
                    </h3>
                    <p className="text-sm">
                      {TEXT.specs.wifi.desc}
                    </p>
                  </div>
                  <div className="static-card p-4">
                    <h3 className="text-accent font-semibold mb-2 flex items-center">
                      <i className="ri-focus-3-line mr-2"></i>
                      {TEXT.specs.autoLevel.title}
                    </h3>
                    <p className="text-sm">
                      {TEXT.specs.autoLevel.desc}
                    </p>
                  </div>
                  <div className="static-card p-4">
                    <h3 className="text-accent font-semibold mb-2 flex items-center">
                      <i className="ri-speed-line mr-2"></i>
                      {TEXT.specs.corexy.title}
                    </h3>
                    <p className="text-sm">
                      {TEXT.specs.corexy.desc}
                    </p>
                  </div>
                  <div className="static-card p-4">
                    <h3 className="text-accent font-semibold mb-2 flex items-center">
                      <i className="ri-inbox-line mr-2"></i>
                      {TEXT.specs.enclosure.title}
                    </h3>
                    <p className="text-sm">
                      {TEXT.specs.enclosure.desc}
                    </p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.specs.enclosureFeatures.title}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2 flex items-center">
                        <i className="ri-camera-line mr-2"></i>
                        {TEXT.specs.enclosureFeatures.camera.title}
                      </h4>
                      <p className="text-sm">{TEXT.specs.enclosureFeatures.camera.desc}</p>
                    </div>
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2 flex items-center">
                        <i className="ri-lightbulb-line mr-2"></i>
                        {TEXT.specs.enclosureFeatures.light.title}
                      </h4>
                      <p className="text-sm">{TEXT.specs.enclosureFeatures.light.desc}</p>
                    </div>
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2 flex items-center">
                        <i className="ri-fire-line mr-2"></i>
                        {TEXT.specs.enclosureFeatures.heatChamber.title}
                      </h4>
                      <p className="text-sm">{TEXT.specs.enclosureFeatures.heatChamber.desc}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.specs.otherFeatures.title}</h3>
                  <div className="space-y-4">
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2 flex items-center">
                        <i className="ri-stack-line mr-2"></i>
                        {TEXT.specs.otherFeatures.ams.title}
                      </h4>
                      <p className="text-sm leading-relaxed">
                        {TEXT.specs.otherFeatures.ams.desc}
                      </p>
                    </div>
                    <div className="static-card p-4">
                      <h4 className="text-accent font-semibold mb-2 flex items-center">
                        <i className="ri-contrast-drop-2-line mr-2"></i>
                        {TEXT.specs.otherFeatures.dualNozzle.title}
                      </h4>
                      <p className="text-sm leading-relaxed">
                        {TEXT.specs.otherFeatures.dualNozzle.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 同時に買っておくべきもの */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-tools-fill text-accent mr-3"></i>
                {TEXT.toBuy.title}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-database-line mr-2"></i>
                    {TEXT.toBuy.filament.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.toBuy.filament.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-tools-line mr-2"></i>
                    {TEXT.toBuy.tools.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.toBuy.tools.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-drop-line mr-2"></i>
                    {TEXT.toBuy.grease.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.toBuy.grease.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-archive-line mr-2"></i>
                    {TEXT.toBuy.storage.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.toBuy.storage.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-sun-line mr-2"></i>
                    {TEXT.toBuy.dryer.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.toBuy.dryer.desc}
                  </p>
                </div>
              </div>
            </section>

            {/* あると便利な周辺アイテム */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-star-line text-accent mr-3"></i>
                {TEXT.convenient.title}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-ruler-line mr-2"></i>
                    {TEXT.convenient.caliper.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.caliper.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-scissors-2-line mr-2"></i>
                    {TEXT.convenient.nipper.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.nipper.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-temp-hot-line mr-2"></i>
                    {TEXT.convenient.hygro.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.hygro.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-contrast-drop-2-line mr-2"></i>
                    {TEXT.convenient.nozzles.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.nozzles.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-stack-line mr-2"></i>
                    {TEXT.convenient.ams.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.ams.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-scissors-cut-line mr-2"></i>
                    {TEXT.convenient.cutter.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.cutter.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-tools-fill mr-2"></i>
                    {TEXT.convenient.driver.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.driver.desc}
                  </p>
                </div>
                <div className="static-card p-4">
                  <h3 className="text-accent font-semibold mb-2 flex items-center">
                    <i className="ri-settings-3-line mr-2"></i>
                    {TEXT.convenient.rotary.title}
                  </h3>
                  <p className="text-sm text-metal-light">
                    {TEXT.convenient.rotary.desc}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BeginnerPage;